import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot,
  updateDoc,
  doc,
  where,
  increment,
  getDoc,
  getDocs,
  limit,
  deleteDoc
} from 'firebase/firestore';
import { db, serverTimestamp } from '../firebase/firebaseConfig';
import CryptoJS from 'crypto-js';

// Encryption key - Must match the key in driver app
const ENCRYPTION_KEY = 'RallysSecureChat2026Key!@#$%';

class AdminChatService {
  /**
   * Encrypt message text
   */
  encryptMessage = (text) => {
    try {
      return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
    } catch (error) {
      console.error('Encryption error:', error);
      return text;
    }
  };

  /**
   * Decrypt message text
   */
  decryptMessage = (encryptedText) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedText;
    }
  };

  /**
   * Get all chat conversations for admin view
   */
  subscribeToConversations(callback) {
    const conversationsRef = collection(db, 'chatConversations');
    const q = query(
      conversationsRef,
      orderBy('lastMessageTime', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const conversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(conversations);
    }, (error) => {
      console.error('Error subscribing to conversations:', error);
    });
  }

  /**
   * Subscribe to messages in a specific conversation
   */
  subscribeToMessages(conversationId, callback) {
    const messagesRef = collection(db, 'chatConversations', conversationId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          text: data.encrypted ? this.decryptMessage(data.text) : data.text,
        };
      });
      callback(messages);
    }, (error) => {
      console.error('Error subscribing to messages:', error);
    });
  }

  /**
   * Get existing conversation for a driver, or create one if it does not exist yet.
   */
  async getOrCreateConversation(driverId, driverName = 'Driver') {
    try {
      if (!driverId) {
        throw new Error('Driver ID is required to create a conversation.');
      }

      const conversationsRef = collection(db, 'chatConversations');
      const existingConversationQuery = query(
        conversationsRef,
        where('driverId', '==', driverId),
        limit(1)
      );
      const existingConversationSnapshot = await getDocs(existingConversationQuery);

      if (!existingConversationSnapshot.empty) {
        return existingConversationSnapshot.docs[0].id;
      }

      const newConversationRef = await addDoc(conversationsRef, {
        driverId,
        driverName,
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTime: null,
        unreadByDriver: 0,
        unreadByAdmin: 0,
        status: 'active',
      });

      return newConversationRef.id;
    } catch (error) {
      console.error('Error getting/creating conversation:', error);
      throw error;
    }
  }

  /**
   * Send a message as admin
   */
  async sendMessage(conversationId, messageText, adminId, adminName = 'Admin') {
    try {
      const messagesRef = collection(db, 'chatConversations', conversationId, 'messages');
      
      // Encrypt the message text
      const encryptedText = this.encryptMessage(messageText);
      const resolvedAdminName = String(adminName || 'Admin').trim() || 'Admin';
      
      const message = {
        text: encryptedText,
        senderId: adminId,
        senderType: 'admin',
        senderName: resolvedAdminName,
        createdAt: serverTimestamp(),
        read: false,
        encrypted: true,
      };

      await addDoc(messagesRef, message);

      // Update conversation
      const conversationRef = doc(db, 'chatConversations', conversationId);
      await updateDoc(conversationRef, {
        lastMessage: encryptedText,
        lastMessageTime: serverTimestamp(),
        unreadByDriver: increment(1),
      });

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Send a bulk chat notification to all drivers.
   */
  async sendBulkMessageToDrivers(
    drivers = [],
    messageText,
    adminId,
    adminName = 'Admin',
    title = ''
  ) {
    const normalizedMessage = String(messageText || '').trim();
    const normalizedTitle = String(title || '').trim();

    if (!normalizedMessage) {
      throw new Error('Bulk notification message is required.');
    }

    if (!adminId) {
      throw new Error('Admin identity is required.');
    }

    const uniqueDriverTargets = new Map();

    (drivers || []).forEach((driver) => {
      const driverId = driver?.userId || driver?.uid || driver?.id || '';
      if (!driverId) {
        return;
      }

      if (!uniqueDriverTargets.has(driverId)) {
        uniqueDriverTargets.set(driverId, {
          driverId,
          driverName:
            driver?.name ||
            driver?.fullName ||
            driver?.driverName ||
            driver?.email ||
            'Driver',
        });
      }
    });

    const bulkText = normalizedTitle
      ? `${normalizedTitle}\n${normalizedMessage}`
      : normalizedMessage;

    const deliveryResults = await Promise.allSettled(
      Array.from(uniqueDriverTargets.values()).map(async (targetDriver) => {
        const conversationId = await this.getOrCreateConversation(
          targetDriver.driverId,
          targetDriver.driverName
        );

        await this.sendMessage(
          conversationId,
          bulkText,
          adminId,
          adminName
        );

        return targetDriver;
      })
    );

    const failedDrivers = [];
    deliveryResults.forEach((deliveryResult, index) => {
      if (deliveryResult.status === 'rejected') {
        const targetDriver = Array.from(uniqueDriverTargets.values())[index];
        failedDrivers.push({
          driverId: targetDriver?.driverId || 'unknown',
          driverName: targetDriver?.driverName || 'Driver',
          error: deliveryResult.reason?.message || 'Failed to deliver message',
        });
      }
    });

    return {
      totalTargets: uniqueDriverTargets.size,
      successCount: uniqueDriverTargets.size - failedDrivers.length,
      failedCount: failedDrivers.length,
      failedDrivers,
    };
  }

  /**
   * Mark messages as read by admin
   */
  async markMessagesAsRead(conversationId) {
    try {
      const conversationRef = doc(db, 'chatConversations', conversationId);
      await updateDoc(conversationRef, {
        unreadByAdmin: 0,
      });
      return true;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  /**
   * Get conversation by ID
   */
  async getConversation(conversationId) {
    try {
      const conversationRef = doc(db, 'chatConversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (conversationSnap.exists()) {
        return {
          id: conversationSnap.id,
          ...conversationSnap.data(),
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }

  /**
   * Update conversation status
   */
  async updateConversationStatus(conversationId, status) {
    try {
      const conversationRef = doc(db, 'chatConversations', conversationId);
      await updateDoc(conversationRef, {
        status,
      });
      return true;
    } catch (error) {
      console.error('Error updating conversation status:', error);
      throw error;
    }
  }

  /**
   * Delete a single message (admin only)
   */
  async deleteMessage(conversationId, messageId) {
    try {
      const messageRef = doc(db, 'chatConversations', conversationId, 'messages', messageId);
      await deleteDoc(messageRef);
      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Delete entire conversation with all messages (admin only)
   */
  async deleteConversation(conversationId) {
    try {
      // First, delete all messages in the conversation
      const messagesRef = collection(db, 'chatConversations', conversationId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      
      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Then delete the conversation document
      const conversationRef = doc(db, 'chatConversations', conversationId);
      await deleteDoc(conversationRef);

      return true;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }
}

export default new AdminChatService();
